import {Component, OnInit, QueryList, ViewChildren, ViewEncapsulation} from '@angular/core';
import {SelectItem} from 'primeng/api';
import {Cabinet} from '../../../models/cabinet';
import {CabinetService} from '../../../services/cabinet.service';
import {ActivatedRoute} from '@angular/router';
import {WorkVisitService} from '../../../services/work-visit.service';
import {WorkVisit} from '../../../models/workVisit';
import {AuthenticationService} from '../../../../authenticationModule/services/authentication.service';
import {UtilsService} from '../../../../appSharedModule/services/utils.service';
import moment from 'moment';
import {environment} from '../../../../../environments/environment';

@Component({
  selector: 'app-work-visit-crud',
  templateUrl: './work-visit-crud.component.html',
  styleUrls: ['./work-visit-crud.component.scss'],
  encapsulation: ViewEncapsulation.None,
  host: {class: 'sym-page'}
})
export class WorkVisitCrudComponent implements OnInit {
  private readonly WORKVISIT_TZ = 'America/New_York';

  @ViewChildren('startTimeCalendar') startTimeCalendars: QueryList<any>;
  @ViewChildren('endTimeCalendar') endTimeCalendars: QueryList<any>;

  hostingManagerUrl = environment.hostingManagerUrl;
  loading = true;
  error = false;
  errorMessage = '';
  success = false;
  successMessage = '';
  mode = 'new';
  cabsOptions: SelectItem[] = [];
  cabsList: Cabinet[];
  selectedOptions: any = [];
  workVisit: WorkVisit = {};
  minDate = moment();
  selectedChildOptions = [];
  coiNotAcceptedError = false;
  coiValid: boolean | null = null;
  dates = [];
  startTimes: (Date | null)[] = [];
  endTimes: (Date | null)[] = [];
  dateRange: any;
  dateChangedProgrammatically = false;
  onBoardingAddress = 'onboarding@mckay-brothers.com';
  jiraComments: any[];
  formPrestine = true;
  displayHelpPopUp = false;
  cabinetId = '';
  selected = false;

  constructor(private cabinetService: CabinetService,
              private route: ActivatedRoute,
              private utilsService: UtilsService,
              private authenticationService: AuthenticationService,
              private workVisitService: WorkVisitService) {
  }

  ngOnInit() {
    this.utilsService.lockUi();
    this.initCabinets();
  }

  private initCabinets() {
    this.cabinetService.getCabinets().subscribe(res => {
      this.cabsList = res;
      res.forEach(cab => {
        if (!this.cabsOptions.map(capOption => capOption.label).includes(cab.technicalPop)) {
          this.cabsOptions.push({
            label: cab.technicalPop,
            value: {
              id: cab.id,
              childOptions: [{
                label: cab.name,
                value: cab.id
              }],
              selectedChildOptions: [],
            }
          } as SelectItem);
        } else {
          const cabOptions = this.cabsOptions.filter(cabOption => cabOption.label === cab.technicalPop)[0];
          cabOptions.value.childOptions.push({
            label: cab.name,
            value: cab.id
          });
        }
      });
      this.cabsOptions.forEach(cabOption => cabOption.value.childOptions.sort((a, b) => a.label.localeCompare(b.label)));
      this.cabsOptions.sort((a, b) => a.label.localeCompare(b.label));
      this.initWorkVisit();
    });
  }

  private initWorkVisit() {
    this.route.paramMap.subscribe(params => {
      const action = params.get('action');
      if (action === 'new') {
        this.workVisit.visitors = [{name: '', email: ''}, {name: '', email: ''}, {name: '', email: ''}];
        this.getLatestWorkVisitVisitors();
        const cabinetId = params.get('id');
        if (cabinetId) {
          this.cabinetId = cabinetId;
          this.cabsOptions.forEach(cabOption => {
            cabOption.value.childOptions.forEach(childOption => {
              if (childOption.value === cabinetId) {
                cabOption.value.selectedChildOptions.push(cabinetId);
                this.onChangeChildOptionCheckbox(cabOption);
              }
            });
          });
        }
        this.mode = 'new';
        this.dateRange = {startDate: moment().toDate(), endDate: moment().toDate()};
        this.buildDays(this.dateRange.startDate, this.dateRange.endDate);
        this.loading = false;
        this.utilsService.unlockUi();
      } else if (action === 'edit') {
        this.mode = 'edit';
        const workVisitId = params.get('id');
        this.getWorkVisit(workVisitId);
      } else if (action === 'show') {
        this.mode = 'show';
        const workVisitId = params.get('id');
        this.getWorkVisit(workVisitId);
        this.getJiraComments(workVisitId);
      } else {
        this.utilsService.unlockUi();
        this.utilsService.redirectToNotFound();
      }
    });
  }

  private getLatestWorkVisitVisitors() {
    this.workVisitService.getLatestWorkVisitVisitors().subscribe(res => {
      if (res.visitors.length === 0) {
        this.workVisit.visitors = [{name: '', email: ''}, {name: '', email: ''}, {name: '', email: ''}];
      } else {
        this.workVisit.visitors = res.visitors;
      }
    });
  }

  private getWorkVisit(workVisitId: string) {
    this.workVisitService.getWorkVisit(workVisitId).subscribe(res => {
      this.workVisit = res;
      this.utilsService.unlockUi();
      if (!['Requested', 'Rejected', 'Draft'].includes(this.workVisit.status) && this.mode !== 'show') {
        this.utilsService.redirectToNotFound();
        return;
      }
      this.dateChangedProgrammatically = true;
      const fromKey = this.toDayKey(this.workVisit.dateFrom);
      const toKey = this.toDayKey(this.workVisit.dateTo);
      this.dateRange = {
        startDate: moment(fromKey, 'YYYY-MM-DD').toDate(),
        endDate: moment(toKey, 'YYYY-MM-DD').toDate(),
      };

      this.buildDays(this.dateRange.startDate, this.dateRange.endDate);
      const apiStarts = this.workVisit.timeDetails?.startTimes ?? [];
      const apiEnds = this.workVisit.timeDetails?.endTimes ?? [];
      apiStarts.forEach((iso: string, i: number) => {
        if (this.startTimes[i] !== undefined) {
          this.startTimes[i] = this.isoUtcToTimeOnlyInEt(iso);
        }
      });
      apiEnds.forEach((iso: string, i: number) => {
        if (this.endTimes[i] !== undefined) {
          this.endTimes[i] = this.isoUtcToTimeOnlyInEt(iso);
        }
      });
      this.cabsOptions.forEach((cabOption) => {
        cabOption.value.childOptions.forEach((childOption) => {
          if (this.workVisit.selectedCabs.includes(childOption.value)) {
            cabOption.value.selectedChildOptions.push(childOption.value);
          }
        });
        if (cabOption.value.selectedChildOptions.length === cabOption.value.childOptions.length) {
          this.selectedOptions.push(cabOption.value);
        }
      });
      this.selectedChildOptions = this.workVisit.selectedCabs;
      this.checkCoiValidity();
      this.loading = false;
    });
  }

  onDateSelect(range: any) {
    if (this.dateChangedProgrammatically) {
      this.dateChangedProgrammatically = false;
      return;
    }

    this.formPrestine = false;

    if (!range?.startDate || !range?.endDate) {
      return;
    }

    this.buildDays(range.startDate, range.endDate);
  }

  addOrEditWorkVisit(isAdd: boolean, isDraft: boolean) {
    if (this.startTimes.length !== this.endTimes.length) {
      this.error = true;
      this.errorMessage = 'You must fill in all the time fields.';
      window.scrollTo({top: 0, left: 0, behavior: 'smooth'});
      return;
    }
    for (let i = 0; i < this.dates.length; i++) {
      if (this.startTimes[i] && !this.endTimes[i]) {
        this.error = true;
        this.errorMessage = 'You must fill in all the time fields.';
        window.scrollTo({top: 0, left: 0, behavior: 'smooth'});
        return;
      }
      if (!this.startTimes[i] && this.endTimes[i]) {
        this.error = true;
        this.errorMessage = 'You must fill in all the time fields.';
        window.scrollTo({top: 0, left: 0, behavior: 'smooth'});
        return;
      }
      if (this.startTimes[i] >= this.endTimes[i]) {
        this.error = true;
        this.errorMessage = 'Invalid time fields.';
        window.scrollTo({top: 0, left: 0, behavior: 'smooth'});
        return;
      }
    }
    this.utilsService.lockUi();
    const timeDetailsForApi = {
      startTimes: this.dates.map((dayKey, i) =>
        this.startTimes[i] ? this.timeOnlyToUtcIso(dayKey, this.startTimes[i] as Date) : null
      ).filter(Boolean),
      endTimes: this.dates.map((dayKey, i) =>
        this.endTimes[i] ? this.timeOnlyToUtcIso(dayKey, this.endTimes[i] as Date) : null
      ).filter(Boolean),
    };

    const newWorkVisit = {
      selectedCabs: this.selectedChildOptions,
      clientComment: this.workVisit.clientComment,
      dateFrom: this.toApiDate(this.dateRange.startDate),
      dateTo: this.toApiDate(this.dateRange.endDate),
      timeDetails: timeDetailsForApi,
      visitors: this.workVisit.visitors,
      status: isDraft ? 'Draft' : 'Requested',
      id: this.workVisit.id
    };
    this.workVisitService.addOrEditWorkVisit(newWorkVisit, isAdd).subscribe({
      next: res => {
        this.workVisit.status = isDraft ? 'Draft' : 'Requested';
        this.utilsService.unlockUi();
        this.error = false;
        window.scrollTo({top: 0, left: 0, behavior: 'smooth'});
        if (!isDraft) {
          location.assign('/#/work-visit/show/' + res.id);
        } else {
          location.assign('/#/work-visit/edit/' + res.id);
          this.successMessage = 'The work visit draft was created.';
          this.success = true;
        }
      }, error: error => {
        if (error.status === 406) {
          this.coiNotAcceptedError = true;
          this.workVisit.status = 'Rejected';
          location.assign('/#/work-visit/show/' + error.error.id);
          this.error = false;
          window.scrollTo({top: 0, left: 0, behavior: 'smooth'});
        } else {
          this.error = true;
          this.errorMessage = 'An error occurred while processing your request';
          window.scrollTo({top: 0, left: 0, behavior: 'smooth'});
        }
        this.utilsService.unlockUi();
      }
    });
  }

  public getSelectedItemsLabel() {
    if (this.selectedChildOptions.length === 0) {
      return 'Select items';
    }
    if (this.selectedChildOptions.length < 3) {
      return this.convertCabValuesToCabLabels(this.selectedChildOptions);
    }
    return this.selectedChildOptions.length + ' items selected';
  }

  private convertCabValuesToCabLabels(cabValuesArray) {
    let selectedCabs = '';
    let technicalPop = '';
    cabValuesArray.forEach(value => {
      const selectedCab = this.cabsList.filter(cab => cab.id === value);
      if (selectedCabs === '') {
        // First item on the loop
        technicalPop = selectedCab[0].technicalPop + ': ';
      }
      if (selectedCab.length > 0) {
        selectedCabs += ', ' + selectedCab[0].name;
      }
    });
    return technicalPop + selectedCabs.substring(2);
  }

  public onClickChildOptionContainer(event) {
    event.stopPropagation();
  }

  public onChangeChildOptionCheckbox(option) {
    this.formPrestine = false;
    this.selectedChildOptions = option.value.selectedChildOptions;
    this.selectedOptions = [];
    if (option.value.childOptions.length === option.value.selectedChildOptions.length) {
      this.selectedOptions.push(option.value);
    }
    this.cabsOptions.forEach((cabOption) => {
      if (option.value.id !== cabOption.value.id) {
        cabOption.value.selectedChildOptions = [];
      }
    });
    this.checkCoiValidity();
  }

  public onChangeMultiSelect($event) {
    this.formPrestine = false;
    this.handleMultiSelectOptionClicked($event);
    this.checkCoiValidity();
  }

  private checkCoiValidity() {
    const cabinetId = this.selectedChildOptions?.[0];
    if (this.mode === 'show' || !cabinetId) {
      this.coiValid = null;
      return;
    }
    this.workVisitService.getCoiValidity(cabinetId).subscribe({
      next: res => {
        if (this.selectedChildOptions?.[0] === cabinetId) {
          this.coiValid = res.valid;
        }
      },
      error: () => this.coiValid = null
    });
  }

  private handleMultiSelectOptionClicked(event) {
    let elementChecked = false;
    event.value.forEach(element => {
      if (element.id === event.itemValue.value.id) {
        elementChecked = true;
      }
    });
    if (elementChecked) {
      // Multiple selection action triggered
      this.selectedOptions = [];
      this.cabsOptions.forEach((option) => {
        if (option.value.id === event.itemValue.value.id) {
          this.selectedOptions.push(option.value);
          option.value.selectedChildOptions = [];
          this.selectedChildOptions = [];
          event.itemValue.value.childOptions.forEach(element => {
            option.value.selectedChildOptions.push(element.value);
            this.selectedChildOptions.push(element.value);
          });
        } else {
          option.value.selectedChildOptions = [];
        }
      });
    } else {
      // Multiple deselection action triggered
      this.cabsOptions.forEach((option) => {
        if (option.value.id === event.itemValue.value.id) {
          this.selectedOptions = [];
          option.value.selectedChildOptions = [];
          this.selectedChildOptions = [];
        }
      });
    }
  }

  getIsMckayUser() {
    return this.authenticationService.getUserCurrent().roles.includes('ROLE_MCKAY');
  }

  getCabListString(): string {
    if (!this.workVisit.selectedCabs) {
      return '';
    }
    let title = '';
    const cabsStringArray = [];
    this.workVisit.selectedCabs.forEach(cabinetUuid => {
      const cab = this.cabsList.filter(cabinet => cabinet.id === cabinetUuid)[0];
      title = cab.commercialPop + '/' + cab.technicalPop + ': ';
      cabsStringArray.push(cab.name);
    });
    return title + cabsStringArray.join(', ');
  }

  cancelWorkVisit() {
    if (confirm('Are you sure you want to cancel the work visit?')) {
      this.workVisitService.cancelWorkVisit(this.workVisit).subscribe(
        () => {
          this.workVisit.status = 'Cancelled';
          location.assign('/#/work-visit/show/' + this.workVisit.id);
          this.success = true;
          this.successMessage = 'Work visit has been cancelled.';
          window.scrollTo({top: 0, left: 0, behavior: 'smooth'});
        }
      );
    }
  }

  copyTime() {
    const cloneTimeOnly = (t: Date) => new Date(2000, 0, 1, t.getHours(), t.getMinutes(), 0, 0);
    for (let i = 1; i < this.dates.length; i++) {
      if (this.startTimes[0]) {
        this.startTimes[i] = cloneTimeOnly(this.startTimes[0] as Date);
      }
      if (this.endTimes[0]) {
        this.endTimes[i] = cloneTimeOnly(this.endTimes[0] as Date);
      }
    }
    this.startTimeCalendars.forEach(c => c.updateInputfield());
    this.endTimeCalendars.forEach(c => c.updateInputfield());
  }

  canSubmit() {
    return (!this.formPrestine || this.workVisit.status === 'Draft')
      && this.selectedChildOptions
      && this.selectedChildOptions.length > 0
      && this.verifyVisitorsFields();
  }

  private verifyVisitorsFields() {
    if (!this.workVisit.visitors[0].name && !this.workVisit.visitors[1].name && !this.workVisit.visitors[2].name) {
      return false;
    }
    for (let i = 0; i < 3; i++) {
      if (this.workVisit.visitors[i].name && (!this.workVisit.visitors[i].email || !this.isEmailValid(this.workVisit.visitors[i].email))) {
        return false;
      }
    }
    return true;
  }

  private getJiraComments(workVisitId: string) {
    this.workVisitService.getJiraComments(workVisitId).subscribe(res => {
      this.jiraComments = res;
    });
  }

  canDeactivate(): boolean {
    if (this.workVisit.status === 'Draft') {
      return window.confirm('This work visit request has not been submitted yet. You may leave this page and come back to Save and Submit this request later.');
    }

    return true;
  }

  getJiraLink(jiraId: string) {
    return 'https://mckay-brothers.atlassian.net/browse/' + jiraId;
  }

  isEmailValid(email: string) {
    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailPattern.test(email);
  }

  alterDisplayHelpPopUp() {
    this.displayHelpPopUp = !this.displayHelpPopUp;
  }

  formatEtDay(dayKey: string): string {
    return moment.tz(dayKey, 'YYYY-MM-DD', this.WORKVISIT_TZ).format('MMM D, YYYY');
  }

  private isoUtcToTimeOnlyInEt(isoUtc: string): Date {
    const m = moment(isoUtc).tz(this.WORKVISIT_TZ);
    return new Date(2000, 0, 1, m.hour(), m.minute(), 0, 0);
  }

  private timeOnlyToUtcIso(dayKey: string, time: Date): string {
    const hh = time.getHours();
    const mm = time.getMinutes();
    return moment.tz(`${dayKey} ${hh}:${mm}`, 'YYYY-MM-DD H:m', this.WORKVISIT_TZ).toISOString();
  }

  private buildDays(startInput: any, endInput: any): void {
    const startKey = this.toDayKey(startInput);
    const endKey = this.toDayKey(endInput);
    const cur = moment(startKey, 'YYYY-MM-DD');
    const last = moment(endKey, 'YYYY-MM-DD');
    this.dates = [];
    while (cur.isSameOrBefore(last, 'day')) {
      this.dates.push(cur.format('YYYY-MM-DD'));
      cur.add(1, 'day');
    }

    this.startTimes = Array(this.dates.length).fill(null);
    this.endTimes = Array(this.dates.length).fill(null);
  }

  private toDayKey(input: any): string {
    if (moment.isMoment(input)) {
      return input.utc().format('YYYY-MM-DD');
    }

    const d = input instanceof Date ? input : new Date(input);
    const y = d.getUTCFullYear();
    const m = String(d.getUTCMonth() + 1).padStart(2, '0');
    const day = String(d.getUTCDate()).padStart(2, '0');

    return `${y}-${m}-${day}`;
  }

  getEasternAbbr(dayKey: string, timeOnly?: Date | null): string {
    const hh = timeOnly ? timeOnly.getHours() : 12;
    const mm = timeOnly ? timeOnly.getMinutes() : 0;
    return moment
      .tz(`${dayKey} ${hh}:${mm}`, 'YYYY-MM-DD H:m', this.WORKVISIT_TZ)
      .format('z');
  }

  private toApiDate(input: any): string {
    return moment(this.toDayKey(input), 'YYYY-MM-DD').format('DD-MM-YYYY');
  }
}
